import cn from 'classnames';
import { isEmpty } from 'lodash';
import { utc } from 'moment-timezone';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { PacingStatus, Tag, TaptapList } from 'sonata-react-components';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import './pacingStatusList.scss';

import { GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';

import type {
  LineItemPacingProgressTotal,
  MultiFlightEntityDiagnosticBasicData,
  MultiFlightEntityPacingProgress,
  MultiFlightEntityType
} from 'sonata-static-storage/lib/common/common.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';

export type EntityPacingStatusEntry = Omit<
  MultiFlightEntityPacingProgress,
  'beginDate' | 'currentDays' | 'daysPct' | 'endDate' | 'target' | 'totalDays'
> & {
  beginDate?: string;
  currentDays?: number;
  daysPct?: number;
  endDate?: string;
  // The goal's own type (budget/clicks/impressions/...), distinct from `type`
  // on EntityPacing which is the entity type (lineItem/insertionOrder/campaignPlan).
  // Absent for entity-level rows, which are always budget-based.
  goalType?: GoalTypeEnum;
  id: string;
  name: string;
  target?: number;
  timeFrame?: string;
  totalDays?: number;
};

export type EntityPacing = EntityPacingStatusEntry & {
  childrenPacingStatus?: EntityPacingStatusEntry[];
  isComposed?: false;
  type: MultiFlightEntityType;
};

export type ComposedEntityPacing = {
  id: EntityPacing['id'];
  isComposed: true;
  pacings: EntityPacing[];
  type: EntityPacing['type'];
};

export type LineItemPacing = EntityPacing & LineItemPacingProgressTotal;

type PacingRowItem = {
  entry: EntityPacing;
  id: string;
};

type Props = {
  currencyInfo: BaseCurrencyInterface;
  entityPacing: EntityPacing | ComposedEntityPacing;
  headerName: string;
  includeDatesColumn: boolean;
  includeTimeframe: boolean;
  isTopLevel: boolean;
  omitExpandables: boolean;
};

type CellsProps = {
  currencyInfo: BaseCurrencyInterface;
  entry: EntityPacingStatusEntry;
  isBold: boolean;
  isExpanded?: boolean;
  isTopLevel: boolean;
  showDates: boolean;
  showDays: boolean;
  showExpandIcon?: boolean;
  showTimeframe: boolean;
};

function PacingEntryCells({
  currencyInfo,
  entry,
  isBold,
  isExpanded,
  isTopLevel,
  showDates,
  showDays,
  showExpandIcon,
  showTimeframe
}: Readonly<CellsProps>): JSX.Element {
  const { formatMessage } = useIntl();
  const countryCode = currencyInfo.countries?.[0];
  // Entity-level rows (no goalType) are always budget/currency; only a
  // non-budget goal type switches to a plain integer.
  const isBudgetGoal = !entry.goalType || entry.goalType === GoalTypeEnum.budget;
  const formatGoalValue = (value: number) =>
    isBudgetGoal ? normalizeWithUnit(value, currencyInfo, 2) : normalize(value, 0, countryCode);

  return (
    <div className={cn('pacingStatusList__cells', { 'pacingStatusList__cells--bold': isBold })}>
      <div className="pacingStatusList__title" title={entry.name}>
        {showExpandIcon && (
          <i
            className={cn('fa-solid fa-chevron-right pacingStatusList__expandIcon', {
              'pacingStatusList__expandIcon--expanded': isExpanded
            })}
          />
        )}
        {entry.name}
      </div>
      {showDates && (
        <div className="pacingStatusList__dates">
          <span>{entry.beginDate ? utc(entry.beginDate).format('YYYY-MM-DD') : '-'}</span>
          <span>-</span>
          <span>{entry.endDate ? utc(entry.endDate).format('YYYY-MM-DD') : '-'}</span>
        </div>
      )}
      {showDays && (
        <div className="pacingStatusList__days">
          <span>{entry.currentDays ?? 0}</span>
          <span>/</span>
          <span className="pacingStatusList__secondaryValue">
            {formatMessage(
              { defaultMessage: '{totalDays} days', id: 'pacingStatusModal.content.common.days.total' },
              { totalDays: entry.totalDays ?? 0 }
            )}
          </span>
          <Tag id={`pacingStatusList-days-${entry.id}`}>{`${normalize(entry.daysPct ?? 0, 2, countryCode)}%`}</Tag>
        </div>
      )}
      {showTimeframe && <div className="pacingStatusList__timeframe">{entry.timeFrame || '-'}</div>}
      <div className="pacingStatusList__pacing">
        {isTopLevel ? (
          '-'
        ) : (
          <Tag
            icon={{ webFont: entry.pacing ? 'fas fa-circle' : 'fas fa-square' }}
            id={`pacingStatusList-pacing-${entry.id}`}
            status={entry.pacing ? 'enabled' : 'default'}
          >
            {entry.pacing
              ? formatMessage({ defaultMessage: 'Enabled', id: 'pacingStatusModal.content.common.pacing.enabled' })
              : formatMessage({ defaultMessage: 'Disabled', id: 'pacingStatusModal.content.common.pacing.disabled' })}
          </Tag>
        )}
      </div>
      <div className="pacingStatusList__target">{formatGoalValue(entry.target ?? 0)}</div>
      <div className="pacingStatusList__current">
        <span>{formatGoalValue(entry.current)}</span>
        <span>/</span>
        <span className="pacingStatusList__secondaryValue">{formatGoalValue(entry.expected)}</span>
      </div>
      <div className="pacingStatusList__progress">
        <span>{`${normalize(entry.currentPct, 2, countryCode)}%`}</span>
        <span>/</span>
        <span className="pacingStatusList__secondaryValue">{`${normalize(entry.expectedPct, 2, countryCode)}%`}</span>
      </div>
    </div>
  );
}

export function PacingStatusTable({
  currencyInfo,
  entityPacing,
  headerName,
  includeDatesColumn,
  includeTimeframe = false,
  isTopLevel = false,
  omitExpandables = false
}: Readonly<Props>): JSX.Element {
  const { formatMessage } = useIntl();
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isLineItem = entityPacing.type === 'lineItem';
  const showDates = includeDatesColumn && !isLineItem;
  const showDays = includeDatesColumn;
  const showTimeframe = includeTimeframe;

  const variant = showTimeframe ? 'withTimeframe' : includeDatesColumn ? 'withDates' : 'compact';

  const rows: EntityPacing[] = useMemo(() => {
    if (!entityPacing.isComposed) {
      return [entityPacing];
    }
    return !isEmpty(entityPacing.pacings) ? entityPacing.pacings : [];
  }, [entityPacing]);

  const rowItems: PacingRowItem[] = useMemo(
    () =>
      rows.map(entry => ({
        entry,
        id: `entity_${entityPacing.type}_${entry.id}_pacing_data`
      })),
    [rows, entityPacing.type]
  );

  return (
    <div className={cn('pacingStatusList', `pacingStatusList--${variant}`)}>
      <TaptapList<PacingRowItem>
        className="pacingStatusList"
        data={rowItems}
        id={`pacingStatusList_${entityPacing.type}_${entityPacing.id}`}
      >
        <TaptapList.ColumnsBar>
          <TaptapList.ColumnsBar.Column className="pacingStatusList__title" description={headerName} id="name" />
          {showDates && (
            <TaptapList.ColumnsBar.Column
              className="pacingStatusList__dates"
              description={formatMessage({
                defaultMessage: 'Dates',
                id: 'pacingStatusModal.content.common.headers.dates'
              })}
              id="dates"
            />
          )}
          {showDays && (
            <TaptapList.ColumnsBar.Column
              className="pacingStatusList__days"
              description={formatMessage({
                defaultMessage: 'Current / Total days',
                id: 'pacingStatusModal.content.common.headers.days'
              })}
              id="days"
            />
          )}
          {showTimeframe && (
            <TaptapList.ColumnsBar.Column
              className="pacingStatusList__timeframe"
              description={formatMessage({
                defaultMessage: 'Timeframe',
                id: 'pacingStatusModal.content.common.headers.timeframe'
              })}
              id="timeframe"
            />
          )}
          <TaptapList.ColumnsBar.Column
            className="pacingStatusList__pacing"
            description={formatMessage({
              defaultMessage: 'Pacing',
              id: 'pacingStatusModal.content.common.headers.pacing'
            })}
            id="pacing"
          />
          <TaptapList.ColumnsBar.Column
            className="pacingStatusList__target"
            description={formatMessage({
              defaultMessage: 'Target',
              id: 'pacingStatusModal.content.common.headers.target'
            })}
            id="target"
          />
          <TaptapList.ColumnsBar.Column
            className="pacingStatusList__current"
            description={formatMessage({
              defaultMessage: 'Current / Expected',
              id: 'pacingStatusModal.content.common.headers.current'
            })}
            id="current"
          />
          <TaptapList.ColumnsBar.Column
            className="pacingStatusList__progress"
            description={formatMessage({
              defaultMessage: 'Progress / Expected',
              id: 'pacingStatusModal.content.common.headers.progress'
            })}
            id="progress"
          />
        </TaptapList.ColumnsBar>
        <TaptapList.RowsList<PacingRowItem>
          emptyStateTitle={formatMessage({
            defaultMessage: 'No pacing data',
            id: `pacingStatusModal.content.${entityPacing.type}Table.emptyState.title`
          })}
        >
          {itemsList =>
            itemsList.map(({ entry, id }) => {
              const isExpandableRow = !omitExpandables && !!entry.childrenPacingStatus?.length;
              return (
                <TaptapList.RowsList.Row
                  id={id}
                  key={id}
                  onCustomRowClick={isExpandableRow ? () => toggleRow(id) : undefined}
                >
                  <PacingStatus
                    entityId={entry.id}
                    getPacing={() =>
                      Promise.resolve({ pacingStatus: entry.pacingStatus } as MultiFlightEntityDiagnosticBasicData)
                    }
                  />
                  <div
                    className={cn('pacingStatusList__rowContent', {
                      'pacingStatusList__rowContent--clickable': isExpandableRow
                    })}
                  >
                    <PacingEntryCells
                      currencyInfo={currencyInfo}
                      entry={entry}
                      isBold
                      isExpanded={expandedRowIds.has(id)}
                      isTopLevel={isTopLevel}
                      showDates={showDates}
                      showDays={showDays}
                      showExpandIcon={isExpandableRow}
                      showTimeframe={showTimeframe}
                    />
                    {isExpandableRow && expandedRowIds.has(id) && (
                      <div className="pacingStatusList__children">
                        {entry.childrenPacingStatus?.map(child => (
                          <PacingEntryCells
                            currencyInfo={currencyInfo}
                            entry={child}
                            isBold={false}
                            isTopLevel={false}
                            key={child.id}
                            showDates={showDates}
                            showDays={showDays}
                            showTimeframe={showTimeframe}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </TaptapList.RowsList.Row>
              );
            })
          }
        </TaptapList.RowsList>
      </TaptapList>
    </div>
  );
}
